import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';
import Navbar from './components/Navbar.js';
import RouteSEOManager from './components/RouteSEOManager.js';
import ScrollToTopOnRouteChange from './components/ScrollToTopOnRouteChange.js';
import { LanguageProvider, useLanguage } from './context/LanguageContext.js';
import { AppConfigProvider, useAppConfig } from './context/AppConfigContext.js';
import GlobalErrorBoundary from './components/GlobalErrorBoundary.js';
import ToolUsageTracker from './components/ToolUsageTracker.js';

const Footer = lazy(() => import('./components/Footer.js'));
const GlobalScrollButtons = lazy(() => import('./components/GlobalScrollButtons.js'));

/* ===============================
   Main Pages
================================ */
const Home = lazy(() => import('./pages/Home.js'));
const Tools = lazy(() => import('./pages/Tools.js'));
const AITools = lazy(() => import('./pages/AITools.js'));
const AIChat = lazy(() => import('./pages/AIChat.js'));
const FootballNews = lazy(() => import('./pages/FootballNews.js'));
const Programming = lazy(() => import('./pages/Programming.js'));
const Gaming = lazy(() => import('./pages/Gaming.js'));
const AIFinder = lazy(() => import('./pages/AIFinder.js'));
const TranslationDashboard = lazy(() => import('./pages/TranslationDashboard.js'));

/* ===============================
   Games
================================ */
const GamesHub = lazy(() => import('./pages/GamesHub.js'));
const MemoryCards = lazy(() => import('./pages/games/MemoryCards.js'));
const NumberPuzzle = lazy(() => import('./pages/games/NumberPuzzle.js'));
const QuickReactionTest = lazy(() => import('./pages/games/QuickReactionTest.js'));
const CodeOutputGuess = lazy(() => import('./pages/games/CodeOutputGuess.js'));
const AIStoryAdventure = lazy(() => import('./pages/games/AIStoryAdventure.js'));
const AIDungeon = lazy(() => import('./pages/games/AIDungeon.js'));
const AIRiddleGenerator = lazy(() => import('./pages/games/AIRiddleGenerator.js'));
const AIGuessTheCharacter = lazy(() => import('./pages/games/AIGuessTheCharacter.js'));
const AIRoastMe = lazy(() => import('./pages/games/AIRoastMe.js'));
const AIQuizGame = lazy(() => import('./pages/games/AIQuizGame.js'));

/* ===============================
   Legal Pages
================================ */
const About = lazy(() => import('./pages/About.js'));
const Contact = lazy(() => import('./pages/Contact.js'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy.js'));
const TermsOfService = lazy(() => import('./pages/TermsOfService.js'));
const HowToUse = lazy(() => import('./pages/HowToUse.js'));

/* ===============================
   Tools
================================ */
const WordCounter = lazy(() => import('./pages/tools/WordCounter.js'));
const JSONFormatter = lazy(() => import('./pages/tools/JSONFormatter.js'));
const PasswordGenerator = lazy(() => import('./pages/tools/PasswordGenerator.js'));
const Base64Encoder = lazy(() => import('./pages/tools/Base64Encoder.js'));
const AgeCalculator = lazy(() => import('./pages/tools/AgeCalculator.js'));
const TextCaseConverter = lazy(() => import('./pages/tools/TextCaseConverter.js'));
const TextCompare = lazy(() => import('./pages/tools/TextCompare.js'));
const LoremIpsum = lazy(() => import('./pages/tools/LoremIpsum.js'));
const URLEncoder = lazy(() => import('./pages/tools/URLEncoder.js'));
const RandomNumber = lazy(() => import('./pages/tools/RandomNumber.js'));
const UnitConverter = lazy(() => import('./pages/tools/UnitConverter.js'));
const WheelOfFortune = lazy(() => import('./pages/tools/WheelOfFortune.js'));
const TranslationTool = lazy(() => import('./pages/tools/TranslationTool.js'));
const ImageCompressor = lazy(() => import('./pages/tools/ImageCompressor.js'));
const ImageResizer = lazy(() => import('./pages/tools/ImageResizer.js'));
const JPGPNGConverter = lazy(() => import('./pages/tools/JPGPNGConverter.js'));
const WebPConverter = lazy(() => import('./pages/tools/WebPConverter.js'));
const ImageCropper = lazy(() => import('./pages/tools/ImageCropper.js'));
const ImageRotator = lazy(() => import('./pages/tools/ImageRotator.js'));
const ImageBase64Tool = lazy(() => import('./pages/tools/ImageBase64Tool.js'));
const MergePDF = lazy(() => import('./pages/tools/MergePDF.js'));
const SplitPDF = lazy(() => import('./pages/tools/SplitPDF.js'));
const CompressPDF = lazy(() => import('./pages/tools/CompressPDF.js'));
const RotatePDF = lazy(() => import('./pages/tools/RotatePDF.js'));
const ExtractPDFPages = lazy(() => import('./pages/tools/ExtractPDFPages.js'));
const BMICalculator = lazy(() => import('./pages/tools/BMICalculator.js'));
const PercentageCalculator = lazy(() => import('./pages/tools/PercentageCalculator.js'));
const DiscountCalculator = lazy(() => import('./pages/tools/DiscountCalculator.js'));
const LoanEMICalculator = lazy(() => import('./pages/tools/LoanEMICalculator.js'));
const VATCalculator = lazy(() => import('./pages/tools/VATCalculator.js'));
const DateDifferenceCalculator = lazy(() => import('./pages/tools/DateDifferenceCalculator.js'));
const TimeDurationCalculator = lazy(() => import('./pages/tools/TimeDurationCalculator.js'));
const RemoveDuplicateLines = lazy(() => import('./pages/tools/RemoveDuplicateLines.js'));
const SlugGenerator = lazy(() => import('./pages/tools/SlugGenerator.js'));
const TextSorter = lazy(() => import('./pages/tools/TextSorter.js'));
const LineBreakRemover = lazy(() => import('./pages/tools/LineBreakRemover.js'));
const JSONFormatterMinifier = lazy(() => import('./pages/tools/JSONFormatterMinifier.js'));
const Base64Tools = lazy(() => import('./pages/tools/Base64Tools.js'));

/* ===============================
   Route Groups (Codex structure)
================================ */
const mainRoutes = [
  { path: '/', element: <Home /> },
  { path: '/tools', element: <Tools /> },
  { path: '/ai-tools', element: <AITools /> },
  { path: '/ai-chat', element: <AIChat /> },
  { path: '/football-news', element: <FootballNews /> },
  { path: '/programming', element: <Programming /> },
  { path: '/gaming', element: <Gaming /> },
  { path: '/ai-finder', element: <AIFinder /> },
  { path: '/games', element: <GamesHub /> },
  { path: '/games/memory-cards', element: <MemoryCards /> },
  { path: '/games/number-puzzle', element: <NumberPuzzle /> },
  { path: '/games/quick-reaction-test', element: <QuickReactionTest /> },
  { path: '/games/code-output-guess', element: <CodeOutputGuess /> },
  { path: '/games/ai-story-adventure', element: <AIStoryAdventure /> },
  { path: '/games/ai-dungeon', element: <AIDungeon /> },
  { path: '/games/ai-riddle-generator', element: <AIRiddleGenerator /> },
  { path: '/games/ai-guess-the-character', element: <AIGuessTheCharacter /> },
  { path: '/games/ai-roast-me', element: <AIRoastMe /> },
  { path: '/games/ai-quiz-game', element: <AIQuizGame /> },
  { path: '/translation-dashboard', element: <TranslationDashboard /> },
];

const legalRoutes = [
  { path: '/about', element: <About /> },
  { path: '/contact', element: <Contact /> },
  { path: '/privacy-policy', element: <PrivacyPolicy /> },
  { path: '/terms-of-service', element: <TermsOfService /> },
  { path: '/how-to-use', element: <HowToUse /> },
];

const SearchResults = lazy(() => import('./pages/SearchResults.js'));
const ToolCategoryPage = lazy(() => import('./pages/ToolCategoryPage.js'));
const ArticlesHub = lazy(() => import('./pages/ArticlesHub.js'));
const ArticlePage = lazy(() => import('./pages/ArticlePage.js'));

const toolRoutes = [
  { path: '/tools/word-counter', element: <WordCounter /> },
  { path: '/tools/json-formatter', element: <JSONFormatter /> },
  { path: '/tools/password-generator', element: <PasswordGenerator /> },
  { path: '/tools/base64-encoder', element: <Base64Encoder /> },
  { path: '/tools/age-calculator', element: <AgeCalculator /> },
  { path: '/tools/text-case-converter', element: <TextCaseConverter /> },
  { path: '/tools/text-compare', element: <TextCompare /> },
  { path: '/tools/lorem-ipsum', element: <LoremIpsum /> },
  { path: '/tools/url-encoder', element: <URLEncoder /> },
  { path: '/tools/random-number', element: <RandomNumber /> },
  { path: '/tools/unit-converter', element: <UnitConverter /> },
  { path: '/tools/wheel-of-fortune', element: <WheelOfFortune /> },
  { path: '/tools/translation-tool', element: <TranslationTool /> },
  { path: '/tools/image-compressor', element: <ImageCompressor /> },
  { path: '/tools/image-resizer', element: <ImageResizer /> },
  { path: '/tools/jpg-png-converter', element: <JPGPNGConverter /> },
  { path: '/tools/webp-converter', element: <WebPConverter /> },
  { path: '/tools/image-cropper', element: <ImageCropper /> },
  { path: '/tools/image-rotator', element: <ImageRotator /> },
  { path: '/tools/image-base64', element: <ImageBase64Tool /> },
  { path: '/tools/merge-pdf', element: <MergePDF /> },
  { path: '/tools/split-pdf', element: <SplitPDF /> },
  { path: '/tools/compress-pdf', element: <CompressPDF /> },
  { path: '/tools/rotate-pdf', element: <RotatePDF /> },
  { path: '/tools/extract-pdf-pages', element: <ExtractPDFPages /> },
  { path: '/tools/bmi-calculator', element: <BMICalculator /> },
  { path: '/tools/percentage-calculator', element: <PercentageCalculator /> },
  { path: '/tools/discount-calculator', element: <DiscountCalculator /> },
  { path: '/tools/loan-emi-calculator', element: <LoanEMICalculator /> },
  { path: '/tools/vat-calculator', element: <VATCalculator /> },
  { path: '/tools/date-difference-calculator', element: <DateDifferenceCalculator /> },
  { path: '/tools/time-duration-calculator', element: <TimeDurationCalculator /> },
  { path: '/tools/remove-duplicate-lines', element: <RemoveDuplicateLines /> },
  { path: '/tools/slug-generator', element: <SlugGenerator /> },
  { path: '/tools/text-sorter', element: <TextSorter /> },
  { path: '/tools/line-break-remover', element: <LineBreakRemover /> },
  { path: '/tools/json-formatter-minifier', element: <JSONFormatterMinifier /> },
  { path: '/tools/base64-tools', element: <Base64Tools /> },
  { path: '/search', element: <SearchResults /> },
  { path: '/tool-category/:slug', element: <ToolCategoryPage /> },
  { path: '/articles', element: <ArticlesHub /> },
  { path: '/blog', element: <ArticlesHub /> },
  { path: '/articles/:slug', element: <ArticlePage /> },
  { path: '/blog/:slug', element: <ArticlePage /> },
];

/* Duplicate Path Guard */
const allRoutePaths = [...mainRoutes, ...legalRoutes, ...toolRoutes].map(r => r.path);
if (new Set(allRoutePaths).size !== allRoutePaths.length) {
  throw new Error('Duplicate route paths detected in App routes configuration.');
}

const RouteLoading = () => {
  const { t } = useLanguage();

  return (
    <div className="mx-auto flex min-h-[45vh] w-full max-w-7xl items-center justify-center px-4">
      <div className="rounded-xl border border-gray-200 bg-white px-6 py-4 text-sm font-medium text-gray-600 shadow-sm">
        {t('routeLoadingPage')}
      </div>
    </div>
  );
};

const AppShell = () => {
  const { isLoaded, config } = useAppConfig();
  const { t } = useLanguage();

  if (!isLoaded && config?.featureFlags?.enableGlobalLoader !== false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="rounded-2xl border border-gray-200 bg-white px-6 py-4 text-sm font-semibold text-gray-700 shadow-sm">{t('appLoadingExperience')}</div>
      </div>
    );
  }

  return (
      <BrowserRouter>
        <GlobalErrorBoundary>
        <div className="App min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
          <ScrollToTopOnRouteChange />
          <RouteSEOManager />
          <ToolUsageTracker />
          <Navbar />

          <main>
            <Suspense fallback={<RouteLoading />}>
              <Routes>
                {[...mainRoutes, ...legalRoutes, ...toolRoutes].map(route => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={route.element}
                  />
                ))}
              </Routes>
            </Suspense>
          </main>

          <Suspense fallback={null}>
            <Footer />
            <GlobalScrollButtons />
          </Suspense>
        </div>
        </GlobalErrorBoundary>
      </BrowserRouter>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AppConfigProvider>
        <AppShell />
      </AppConfigProvider>
    </LanguageProvider>
  );
}

export default App;
